import { Localization } from "@viamap/viamap2-common";
import { DataCollector } from "src/dataCollector/DataCollector";
import { wfsEndpoints } from "src/dataCollector/WFSCollectorEndpoints";
import { Conflict, ConflictSearchParams } from "./ConflictSearchResults";
import { message } from "./Message";
import { featureCollection } from "@turf/helpers";
import union from "@turf/union";
import buffer from "@turf/buffer";
import { LayerInfo } from "src/common/managers/Types";
import { booleanIntersects } from "@turf/turf";


export async function searchSingleLayerForConflicts(
   geom: any,
   endpoint: keyof typeof wfsEndpoints,
   featureType: string,
   options?: {
      whoAmI?: string,
      filter?: any
   }
): Promise<any> {
   return await DataCollector.WFS.getFeatureIntersect(endpoint, featureType as keyof typeof wfsEndpoints[typeof endpoint]["featureTypes"], geom as any, {
      whoami: wfsEndpoints[endpoint]?.["whoAmI"] ? (options?.whoAmI ?? undefined) : undefined
   }).then((res) => {
      if (options?.filter) {
         let filtered = filterFeaturesWithMapFilter(res.features, options.filter);
         return {
            ...res,
            features: filtered,
            metadata: {
               ...res.metadata,
               numberMatched: filtered.length + "",
               numberReturned: filtered.length + ""
            }
         };
      } else {
         return res;
      }
   })
}

function featureMatchesMapFilter(feature: any, filter: any): boolean {
   let actualFilter = filter;
   if (Array.isArray(filter[0])) {
      actualFilter = ["all", ...filter];
   }
   switch (actualFilter[0]) {
      case "any": {
         return actualFilter.slice(1).some((f: any) => featureMatchesMapFilter(feature, f));
      }
      case "all": {
         return actualFilter.slice(1).every((f: any) => featureMatchesMapFilter(feature, f));
      }
      case "==": {
         let [_, [_get, field], value] = actualFilter;
         return feature.properties?.[field] == value;
      }
      case "!=": {
         let [_, [_get, field], value] = actualFilter;
         return feature.properties?.[field] != value;
      }
      case ">": {
         let [_, [_get, field], value] = actualFilter;
         return feature.properties?.[field] > value;
      }
      case "<": {
         let [_, [_get, field], value] = actualFilter;
         return feature.properties?.[field] < value;
      }
      case ">=": {
         let [_, [_get, field], value] = actualFilter;
         return feature.properties?.[field] >= value;
      }
      case "<=": {
         let [_, [_get, field], value] = actualFilter;
         return feature.properties?.[field] <= value;
      }
      default:
         return true;
   }
}

function filterFeaturesWithMapFilter(features: any[], filter: any) {
   return features.filter(feature => featureMatchesMapFilter(feature, filter));
}

export async function handleClickProfileSearch(dataLayers: LayerInfo[], searchParams: ConflictSearchParams, setConflicts: React.Dispatch<React.SetStateAction<Conflict[]>>, abortController?: AbortController) {
   let layers = searchParams.layers ?? []
   if (searchParams.pages) {
      layers = searchParams.pages.map(p => p.layers).flat();
   }
   let conflicts: Conflict[] = (layers).map((layer) => ({
      ...layer,
      conflict: null,
      intersect: null,
      progress: 0,
      err: "",
      matchedCount: -1
   }));
   let dataLayerConflicts: Conflict[] = (dataLayers).map((layer) => ({
      source: Localization.getText("Data layer"),
      layer: layer.datasetname,
      title: layer.datasetname,
      conflict: null,
      intersect: null,
      progress: 0,
      err: "",
      matchedCount: -1
   }));
   setConflicts(conflicts.concat(dataLayerConflicts));
   let geom = forceFeatureCollection(geomWithBuffer(searchParams.geom, searchParams.buffer));

   for (let i = 0; i < dataLayerConflicts.length; i++) {
      if (abortController?.signal.aborted) {
         continue;
      }
      let foundConflicts = findConflictsInDataLayer(geom, dataLayers[i]);
      dataLayerConflicts = dataLayerConflicts.map((conflict, index) => {
         if (index === i) {
            return {
               ...conflict,
               conflict: foundConflicts,
               intersect: geom,
               err: "",
               progress: 1,
               matchedCount: foundConflicts?.features.length || 0
            };
         }
         return conflict;
      });
      setConflicts(conflicts ? conflicts.concat(dataLayerConflicts) : dataLayerConflicts);
   };


   let perSource = Object.groupBy(conflicts, c => c.source)
   let resultingConflicts: Conflict[][] = await Promise.all(Object.keys(perSource)!.map(async (source) => {
      let sourceLayers = perSource[source];
      if (!sourceLayers) {
         message.error(Localization.getFormattedText("No layers found for source {source}", { source }));
         return [] as Conflict[];
      }
      for (let i = 0; i < sourceLayers.length; i++) {
         if (abortController?.signal.aborted) {
            continue;
         }
         let layer = sourceLayers[i];
         let searchResult: WFSFeatureCollection | null = null;
         try {

            searchResult = await searchSingleLayerForConflicts(geom, layer.source as keyof typeof wfsEndpoints, layer.layer as string, {
               filter: layer.filter,
            });
            if (!searchResult) {
               message.error(Localization.getFormattedText("No search result for layer {layer} in source {source}", { layer: layer.layer, source: layer.source }));
            }
            sourceLayers[i] = {
               ...sourceLayers[i],
               conflict: searchResult!,
               progress: 1,
               matchedCount: parseInt(searchResult?.metadata?.numberMatched || "0") || (searchResult?.features && searchResult.features.length) || 0
            };
         } catch (e) {
            sourceLayers[i] = {
               ...sourceLayers[i],
               conflict: null,
               progress: 1,
               err: e instanceof Error ? e.message : String(e),
               matchedCount: -1
            };
         }
         setConflicts((c) => {
            let newConflicts = c.map(conflict => {
               if (conflict.source === sourceLayers[i].source && conflict.layer === sourceLayers[i].layer) {
                  return sourceLayers[i];
               }
               return conflict;
            })
            return newConflicts;
         });
      }
      return sourceLayers;
   }));
   setConflicts(() => resultingConflicts.flat().concat(dataLayerConflicts));
}

type WFSFeatureCollection = ReturnType<typeof featureCollection> & { metadata?: { endpoint?: string; numberMatched?: string; numberReturned?: number } };
export function geomWithBuffer(geom: any, bufferDistance?: number) {
   if (!geom) return featureCollection([]);
   if (bufferDistance) {
      if (geom.features.length > 1) return union(buffer(geom, bufferDistance, { units: 'meters' }) as any);
      return buffer(geom, bufferDistance, { units: 'meters' });
   }
   return geom;
}

export function forceFeatureCollection(geom: any) {
   if (geom.type === "FeatureCollection") return geom as WFSFeatureCollection;
   return featureCollection(geom.features ?? [geom]);
}

export function findConflictsInDataLayer(geom: any, dataLayer: LayerInfo): GeoJSON.FeatureCollection | null {
   if (!geom) {
      return null;
   }
   if (!dataLayer.geoJson) {
      return null;
   }
   let conflicts = dataLayer.geoJson.features.filter((feature) => isOverlappingFeatureCollection(geom, feature));
   if (conflicts.length > 0) {
      return featureCollection(conflicts);
   }
   return null;
}

export function isOverlappingFeatureCollection(featureCollection: GeoJSON.FeatureCollection, featureTarget: GeoJSON.Feature): boolean {
   for (let feature of featureCollection.features) {
      if (isOverlappingFeature(feature, featureTarget)) {
         return true;
      }
   }
   return false
}

export function isOverlappingFeature(feature1: GeoJSON.Feature, feature2: GeoJSON.Feature): boolean {
   if (booleanIntersects(feature1, feature2)) {
      return true;
   }
   return false;
}